/**
 * Shared password rules for the profile sheet's auth views.
 * Lifted from RegisterForm/WelcomePage (the strict variant, incl. special
 * character) — this intentionally tightens the laxer legacy reset/change
 * validators so every password path enforces the same policy.
 */

const SPECIAL = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/;

export const validatePassword = (password: string): string | null => {
  if (password.length < 8) {
    return 'Password must be at least 8 characters';
  }
  if (!/[A-Z]/.test(password)) {
    return 'Password must contain at least one uppercase letter';
  }
  if (!/[a-z]/.test(password)) {
    return 'Password must contain at least one lowercase letter';
  }
  if (!/[0-9]/.test(password)) {
    return 'Password must contain at least one number';
  }
  if (!SPECIAL.test(password)) {
    return 'Password must contain at least one special character (!@#$%^&*etc.)';
  }
  return null;
};

export interface PasswordStrength {
  /** 0–100 for the ambient progress bar */
  strength: number;
  /** Mono microcopy label */
  label: '' | 'WEAK' | 'OK' | 'STRONG';
  /** True only for the weak tier — drives the bar's alert color */
  weak: boolean;
}

export const getPasswordStrength = (password: string): PasswordStrength => {
  if (!password) return { strength: 0, label: '', weak: false };

  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (SPECIAL.test(password)) score++;

  if (score <= 3) return { strength: 33, label: 'WEAK', weak: true };
  if (score <= 5) return { strength: 66, label: 'OK', weak: false };
  return { strength: 100, label: 'STRONG', weak: false };
};
